import { useState } from 'react'
import { Brain, Lock, TrendingUp, AlertTriangle, RefreshCw, ChevronDown, ChevronUp, History } from 'lucide-react'
import api from '../lib/api'

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CFG = {
  ACCEPTED:  { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  REJECTED:  { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
  LIMITED:   { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  EVOLVING:  { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  TENTATIVE: { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.TENTATIVE
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px',
      fontSize: '0.62rem', fontWeight: 700, borderRadius: 3,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
    }}>
      {status}
    </span>
  )
}

// ── Confidence badge ──────────────────────────────────────────────────────────

function ConfBadge({ confidence }) {
  const cfg = {
    HIGH:   { bg: '#dcfce7', color: '#15803d', label: 'HIGH' },
    MEDIUM: { bg: '#fef9c3', color: '#854d0e', label: 'MED'  },
    LOW:    { bg: '#f1f5f9', color: '#64748b', label: 'LOW'  },
  }[confidence] || { bg: '#f1f5f9', color: '#64748b', label: '?' }

  return (
    <span style={{
      display: 'inline-block', padding: '1px 5px',
      fontSize: '0.6rem', fontWeight: 700, borderRadius: 3,
      background: cfg.bg, color: cfg.color, marginLeft: 4,
    }}>
      {cfg.label}
    </span>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ icon: Icon, title, color, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ marginBottom: '1rem' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '0.4rem 0', color,
        }}
      >
        <Icon size={13} />
        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1, textAlign: 'left' }}>
          {title}{count != null ? ` (${count})` : ''}
        </span>
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
      {open && <div style={{ paddingLeft: 4 }}>{children}</div>}
    </div>
  )
}

// ── Locked field row ──────────────────────────────────────────────────────────

function LockedRow({ label, value }) {
  if (!value || (Array.isArray(value) && !value.length)) return null
  const display = Array.isArray(value) ? value.join(', ') : value
  return (
    <div style={{ marginBottom: '0.4rem' }}>
      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <div style={{ fontSize: '0.8rem', color: '#111827', marginTop: 1 }}>{display}</div>
    </div>
  )
}

// ── Framework history timeline ────────────────────────────────────────────────

function FrameworkHistory({ history }) {
  if (!history?.length) return null
  return (
    <div style={{ marginTop: 6, paddingLeft: 8, borderLeft: '2px solid #e5e7eb' }}>
      {history.map((h, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: '0.62rem', color: '#9ca3af', minWidth: 32 }}>Ch.{h.chapter}</span>
          <StatusBadge status={h.status} />
          {h.note && (
            <span style={{ fontSize: '0.68rem', color: '#6b7280', fontStyle: 'italic' }}>
              {h.note}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Individual framework row ──────────────────────────────────────────────────

function FrameworkRow({ fw }) {
  const [showHistory, setShowHistory] = useState(false)
  const hasHistory = fw.history?.length > 0
  const cfg = STATUS_CFG[fw.status] || STATUS_CFG.TENTATIVE

  return (
    <div style={{
      marginBottom: '0.5rem', padding: '0.5rem 0.625rem',
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 5,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', flex: 1 }}>{fw.name}</span>
        <StatusBadge status={fw.status} />
        <ConfBadge confidence={fw.confidence} />
        {hasHistory && (
          <button
            onClick={() => setShowHistory(o => !o)}
            title="Toggle chapter history"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#9ca3af' }}
          >
            <History size={11} />
          </button>
        )}
      </div>

      {fw.context && (
        <p style={{ fontSize: '0.72rem', color: '#374151', margin: '3px 0 0', fontStyle: 'italic' }}>
          {fw.context}
        </p>
      )}

      {showHistory && <FrameworkHistory history={fw.history} />}
    </div>
  )
}

// ── Frameworks section ────────────────────────────────────────────────────────

function FrameworksSection({ frameworks }) {
  if (!frameworks?.length) return (
    <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>No frameworks tracked yet.</p>
  )

  // Group by status for display order
  const order = ['ACCEPTED', 'LIMITED', 'EVOLVING', 'TENTATIVE', 'REJECTED']
  const grouped = order.reduce((acc, s) => {
    const items = frameworks.filter(f => f.status === s)
    if (items.length) acc.push({ status: s, items })
    return acc
  }, [])

  return (
    <div>
      {grouped.map(({ status, items }) => (
        <div key={status} style={{ marginBottom: '0.625rem' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: STATUS_CFG[status]?.color || '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            {status} ({items.length})
          </div>
          {items.map((fw, i) => <FrameworkRow key={i} fw={fw} />)}
        </div>
      ))}
    </div>
  )
}

// ── Locked section ────────────────────────────────────────────────────────────

function LockedSection({ locked }) {
  if (!locked) return null
  const hasContent = locked.methodology || locked.researchPhilosophy ||
    locked.frameworks?.length ||
    locked.coreVariables?.dependent?.length || locked.coreVariables?.independent?.length ||
    locked.researchObjectives?.length

  if (!hasContent) return (
    <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>No positions locked yet. Generate a chapter to initialise.</p>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '0.75rem' }}>
        <LockedRow label="Research Topic"      value={locked.researchTopic} />
        <LockedRow label="Methodology"         value={locked.methodology} />
        <LockedRow label="Research Philosophy" value={locked.researchPhilosophy} />
        <LockedRow label="Objectives"          value={locked.researchObjectives} />
        <LockedRow label="Dependent Variables"   value={locked.coreVariables?.dependent} />
        <LockedRow label="Independent Variables" value={locked.coreVariables?.independent} />
        <LockedRow label="Control Variables"     value={locked.coreVariables?.control} />
      </div>

      {locked.frameworks?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Theoretical Frameworks
          </div>
          <FrameworksSection frameworks={locked.frameworks} />
        </div>
      )}
    </div>
  )
}

// ── Evolving section ──────────────────────────────────────────────────────────

function EvolvingSection({ evolving }) {
  if (!evolving) return null
  const summaries = Object.entries(evolving.chapterSummaries || {})
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
  const positions = evolving.keyPositions || []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {summaries.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Chapter Summaries
          </div>
          {summaries.map(([n, s]) => (
            <div key={n} style={{ marginBottom: '0.5rem', paddingLeft: '0.625rem', borderLeft: '2px solid #6366f1' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6366f1' }}>Ch.{n}</span>
              <p style={{ fontSize: '0.78rem', color: '#374151', margin: '2px 0 0', lineHeight: 1.5 }}>{s}</p>
            </div>
          ))}
        </div>
      )}

      {positions.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
            Key Analytical Positions
          </div>
          {positions.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 4 }}>
              <span style={{ fontSize: '0.78rem', color: '#374151', flex: 1, lineHeight: 1.45 }}>{p.text}</span>
              <ConfBadge confidence={p.confidence} />
            </div>
          ))}
        </div>
      )}

      {evolving.sampleDescription && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Research Sample
          </div>
          <p style={{ fontSize: '0.78rem', color: '#374151' }}>{evolving.sampleDescription}</p>
        </div>
      )}

      {summaries.length === 0 && positions.length === 0 && !evolving.sampleDescription && (
        <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>No evolving data yet.</p>
      )}
    </div>
  )
}

// ── Contradiction section ─────────────────────────────────────────────────────

function ContradictionSection({ contradictions }) {
  if (!contradictions?.length) return (
    <p style={{ fontSize: '0.78rem', color: '#16a34a' }}>No contradictions detected ✓</p>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {contradictions.map((c, i) => (
        <div key={i} style={{
          padding: '0.5rem 0.75rem',
          background: '#fff7ed', border: '1px solid #fed7aa',
          borderLeft: '3px solid #ea580c', borderRadius: '0 4px 4px 0',
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#ea580c', textTransform: 'uppercase' }}>
            Ch.{c.chapter} · {c.type?.replace(/_/g, ' ')} · {c.severity}
          </div>
          <p style={{ fontSize: '0.78rem', color: '#374151', margin: '2px 0 0' }}>{c.description}</p>
          <p style={{ fontSize: '0.68rem', color: '#9ca3af', margin: '2px 0 0' }}>
            {new Date(c.detected).toLocaleString()}
          </p>
        </div>
      ))}
    </div>
  )
}

// ── Root component ────────────────────────────────────────────────────────────

export default function ThesisMemoryPanel({ thesisId }) {
  const [state,    setState]    = useState('idle')
  const [memory,   setMemory]   = useState(null)
  const [expanded, setExpanded] = useState(false)

  async function load() {
    setState('loading')
    try {
      const data = await api.get(`/thesis/${thesisId}/memory`)
      setMemory(data)
      setState('done')
      setExpanded(true)
    } catch {
      setState('error')
    }
  }

  const contradictionCount = memory?.contradictions?.length ?? 0
  const frameworkCount     = memory?.locked?.frameworks?.length ?? 0

  return (
    <div>
      {/* Trigger row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
        <button
          onClick={state === 'idle' ? load : () => setExpanded(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0.5rem 0.875rem',
            background: '#1e3a5f', color: '#93c5fd',
            border: '1px solid rgba(147,197,253,0.3)', borderRadius: 8,
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {state === 'loading'
            ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <Brain size={13} />
          }
          {state === 'idle' ? 'Open Memory Audit' : expanded ? 'Hide Audit' : 'Show Audit'}
        </button>

        {contradictionCount > 0 && (
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px',
            background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa', borderRadius: 12,
          }}>
            ⚠ {contradictionCount} conflict{contradictionCount > 1 ? 's' : ''}
          </span>
        )}

        {state === 'done' && (
          <button onClick={load} title="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
            <RefreshCw size={12} />
          </button>
        )}
      </div>

      {state === 'error' && (
        <p style={{ fontSize: '0.78rem', color: '#dc2626' }}>
          Memory unavailable — generate at least one chapter first.
        </p>
      )}

      {state === 'done' && expanded && (
        <div style={{
          border: '1px solid #e5e7eb', borderRadius: 8,
          overflow: 'hidden', background: '#fff',
        }}>
          {/* Locked */}
          <div style={{ padding: '0.875rem', borderBottom: '1px solid #e5e7eb' }}>
            <Section icon={Lock} title="Locked Positions" color="#15803d" defaultOpen={true}>
              <LockedSection locked={memory?.locked} />
            </Section>
          </div>

          {/* Evolving */}
          <div style={{ padding: '0.875rem', borderBottom: '1px solid #e5e7eb' }}>
            <Section icon={TrendingUp} title="Evolving Knowledge" color="#6366f1" defaultOpen={true}>
              <EvolvingSection evolving={memory?.evolving} />
            </Section>
          </div>

          {/* Contradictions */}
          <div style={{ padding: '0.875rem' }}>
            <Section
              icon={AlertTriangle}
              title="Contradiction Log"
              color={contradictionCount > 0 ? '#ea580c' : '#6b7280'}
              count={contradictionCount}
              defaultOpen={contradictionCount > 0}
            >
              <ContradictionSection contradictions={memory?.contradictions} />
            </Section>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
